<script setup lang="ts">
const history = [
  { year: '2019', voted: 83, total: 132, rate: 62.9 },
  { year: '2020', voted: 145, total: 193, rate: 75.1 },
  { year: '2021', voted: 189, total: 225, rate: 84.0 },
  { year: '2022', voted: 137, total: 169, rate: 81.1 },
  { year: '2023', voted: 111, total: 141, rate: 78.7 },
  { year: '2024', voted: 87, total: 95, rate: 91.6 },
  { year: '2025', voted: 98, total: 128, rate: 76.6 },
  { year: '2026', voted: 32, total: 62, rate: 51.6 },
]
</script>

<template>
  <section class="senate-audit" aria-labelledby="senate-audit-title">
    <div class="wrap senate-audit__shell">
      <header class="senate-audit__header">
        <div>
          <span class="senate-audit__eyebrow">AUDITORIA DO MANDATO // DADOS DO SENADO</span>
          <p class="senate-audit__command" aria-hidden="true">$ aplicar_regra --pagamento-por-hora --ao-autor</p>
        </div>
        <span class="senate-audit__stamp">ATUALIZADO<br>18.OUT.2026</span>
      </header>

      <div class="senate-audit__opening">
        <h2 id="senate-audit-title">Ele propôs pagar o trabalhador por hora.<br><em>Aplique a régua a ele.</em></h2>
        <p>Flávio recebe <strong>R$ 46.366,19 brutos por mês</strong> como senador. Mas o Senado não publica quantas horas ele trabalhou. Não existe relógio de ponto aberto ao cidadão. O que existe é o registro de cada decisão nominal — e, em 2026, o voto dele desapareceu de quase metade do painel.</p>
      </div>

      <div class="senate-audit__clock">
        <div>
          <span>HORAS TRABALHADAS</span>
          <strong>NÃO INFORMADAS</strong>
          <small>O Portal da Transparência divulga remuneração e presença, não uma jornada parlamentar em horas.</small>
        </div>
        <p>Sem cartão de ponto, medimos a entrega que deixa rastro: <b>voto registrado</b>, <b>presença sem voto</b>, <b>não comparecimento</b> e <b>ausência justificada</b>.</p>
      </div>

      <section class="senate-audit__year" aria-labelledby="senate-2026-title">
        <div class="senate-audit__year-title">
          <span>2026 // 1º JAN. A 18 OUT.</span>
          <h3 id="senate-2026-title">O Senado abriu 62 decisões nominais.<br>Flávio votou em 32.</h3>
        </div>
        <div class="senate-audit__counts">
          <article class="is-vote"><strong>32</strong><span>votos registrados</span></article>
          <article class="is-silent"><strong>16</strong><span>presente, sem voto</span></article>
          <article class="is-absent"><strong>10</strong><span>não compareceu</span></article>
          <article class="is-justified"><strong>4</strong><span>ausências justificadas</span></article>
        </div>
        <p class="senate-audit__verdict"><strong>51,6%.</strong> Essa foi a participação dele nas votações nominais do ano. Para quem quer impor ao trabalhador a precisão da hora, entregar voto em pouco mais da metade das decisões é uma afronta.</p>
      </section>

      <section class="senate-audit__history" aria-labelledby="senate-history-title">
        <div class="senate-audit__section-heading">
          <span>SÉRIE COMPLETA // SEM ESCONDER OS ANOS MELHORES</span>
          <h3 id="senate-history-title">Em 2026, a entrega registrada desabou.</h3>
          <p>Percentual de votações nominais em que Flávio registrou voto. “Presente, sem voto” não entra como voto.</p>
        </div>
        <div class="senate-audit__bars" role="img" aria-label="Participação de Flávio Bolsonaro em votações nominais: 62,9% em 2019, 75,1% em 2020, 84% em 2021, 81,1% em 2022, 78,7% em 2023, 91,6% em 2024, 76,6% em 2025 e 51,6% em 2026 até 18 de outubro.">
          <div v-for="item in history" :key="item.year" :class="{ current: item.year === '2026' }">
            <strong>{{ item.rate.toFixed(1).replace('.', ',') }}%</strong>
            <span class="senate-audit__bar"><i :style="{ height: `${item.rate}%` }"></i></span>
            <b>{{ item.year }}</b>
            <small>{{ item.voted }}/{{ item.total }} votos</small>
          </div>
        </div>
      </section>

      <section class="senate-audit__pay" aria-labelledby="senate-pay-title">
        <div class="senate-audit__section-heading">
          <span>AGORA, A CONTA QUE ELE QUER PARA OS OUTROS</span>
          <h3 id="senate-pay-title">R$ 46 mil garantidos. Entrega pela metade.</h3>
        </div>
        <div class="senate-audit__pay-grid">
          <article><span>Subsídio mensal bruto</span><strong>R$ 46.366,19</strong><small>valor oficial de senador</small></article>
          <article><span>Conversão em 220 horas</span><strong>R$ 210,76/h</strong><small>subsídio ÷ jornada mensal comum</small></article>
          <article><span>Mínimo de 2026 em 220 horas</span><strong>R$ 7,37/h</strong><small>R$ 1.621 ÷ 220</small></article>
          <article class="is-ratio"><span>A distância</span><strong>28,6×</strong><small>o valor-hora do salário mínimo</small></article>
        </div>
        <div class="senate-audit__apply-rule">
          <span>SE O PAGAMENTO ACOMPANHASSE O PERCENTUAL DE VOTOS REGISTRADOS</span>
          <div><s>R$ 46.366,19</s><strong>R$ 23.930,94</strong></div>
          <p>Aplicação direta dos 51,6% registrados até 18 de outubro sobre o subsídio mensal. A lei não paga senador por voto. Esta é a régua política de Flávio — pagamento proporcional à entrega — devolvida ao autor.</p>
        </div>
        <div class="senate-audit__cost">
          <span>JAN. A SET. // NOVE MESES COMPLETOS</span>
          <strong>R$ 14.389,51</strong>
          <p>de subsídio bruto por voto registrado no período. A conta é simples: nove salários de R$ 46.366,19 divididos pelos 29 votos que ele efetivamente registrou.</p>
        </div>
      </section>

      <section class="senate-audit__missing" aria-labelledby="senate-missing-title">
        <div class="senate-audit__section-heading">
          <span>ONDE AS DEZ AUSÊNCIAS SEM COMPARECIMENTO APARECEM</span>
          <h3 id="senate-missing-title">Não foi numa votação qualquer. Foram dez decisões.</h3>
        </div>
        <div>
          <article><time datetime="2026-05-20">20 MAI.</time><strong>9 decisões</strong><p>Indicações para embaixadas e para a Comissão de Valores Mobiliários, inclusive sua presidência.</p><b>NÃO COMPARECEU</b></article>
          <article><time datetime="2026-05-27">27 MAI.</time><strong>1 decisão</strong><p>PLP 11/2026, sobre benefícios tributários para entidades sem fins lucrativos.</p><b>NÃO COMPARECEU</b></article>
        </div>
      </section>

      <footer class="senate-audit__footer">
        <p>Quem recebe quase <strong>29 salários mínimos por mês</strong> para decidir o destino do país não pode vender precariedade ao trabalhador e entregar silêncio no painel ao contribuinte.</p>
        <nav aria-label="Fontes da auditoria">
          <a href="https://legis.senado.leg.br/dadosabertos/votacao?codigoParlamentar=5894&amp;v=2" target="_blank" rel="noreferrer">Abrir votações no Senado ↗</a>
          <a href="https://www12.senado.leg.br/transparencia/rh/segp/estrutura-remuneratoria-dos-parlamentares" target="_blank" rel="noreferrer">Abrir remuneração ↗</a>
          <a href="https://www12.senado.leg.br/assessoria-de-imprensa/tutorial-de-verificacao-da-assiduidade-dos-senadores" target="_blank" rel="noreferrer">Como o Senado conta presença ↗</a>
          <RouterLink to="/casos/escala-6x1-e-pagamento-por-hora">Ver a fala sobre pagamento por hora →</RouterLink>
        </nav>
      </footer>
    </div>
  </section>
</template>
